<template>
    <transition name="modal-pop">
        <div v-if="store.isOpen" class="ai-modal">
            <div class="ai-modal__header">
                <span v-if="live.state.value === 'active'" class="ai-modal__live-dot" :class="{ speaking: live.aiSpeaking.value, listening: live.userSpeaking.value }" />
                <button
                    v-else-if="store.messages.length > 0"
                    class="ai-modal__hbtn"
                    @click="resetChat"
                    title="Почати спочатку"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                    </svg>
                </button>
                <span v-else class="ai-modal__hbtn ai-modal__hbtn--spacer" />

                <div class="ai-modal__tabs">
                    <button
                        class="ai-modal__tab"
                        :class="{ active: store.activeTab === 'chat' }"
                        @click="store.setTab('chat')"
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                        Чат
                    </button>
                    <button
                        class="ai-modal__tab"
                        :class="{ active: store.activeTab === 'voice' }"
                        @click="store.setTab('voice')"
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="9" y="2" width="6" height="11" rx="3"/>
                            <path d="M5 10a7 7 0 0 0 14 0"/>
                            <line x1="12" y1="19" x2="12" y2="22"/>
                        </svg>
                        Голос
                    </button>
                    <span class="ai-modal__tab-indicator" :style="tabIndicatorStyle" />
                </div>

                <div class="ai-modal__hright">
                    <button
                        class="ai-modal__hbtn"
                        :class="{ active: store.ttsEnabled, speaking: store.isSpeaking }"
                        @click="store.toggleTts()"
                        :title="store.ttsEnabled ? 'Вимкнути голос' : 'Увімкнути голос'"
                    >
                        <svg v-if="store.ttsEnabled" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                            <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                            <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                        </svg>
                        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                            <line x1="23" y1="9" x2="17" y2="15"/>
                            <line x1="17" y1="9" x2="23" y2="15"/>
                        </svg>
                    </button>
                    <button class="ai-modal__hbtn" @click="store.close" title="Закрити">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                            <path d="M6 6L18 18M6 18L18 6"/>
                        </svg>
                    </button>
                </div>
            </div>

            <!-- ═══════ CHAT TAB ═══════ -->
            <template v-if="store.activeTab === 'chat'">
                <div class="ai-modal__body" ref="messagesEl">
                    <template v-if="store.messages.length === 0">
                        <div class="ai-modal__welcome">
                            <div class="ai-modal__welcome-orb">
                                <span v-if="welcomeIcon.type === 'emoji'" class="ai-modal__welcome-emoji">{{ welcomeIcon.emoji }}</span>
                                <svg v-else viewBox="0 0 40 40" fill="none" v-html="welcomeIcon.path" />
                            </div>
                            <p class="ai-modal__welcome-hi">Привіт!</p>
                            <p class="ai-modal__welcome-name">
                                Я <strong>{{ store.widgetTitle || 'AI Асистент' }}</strong>!
                            </p>
                            <p class="ai-modal__welcome-sub">
                                {{ store.widgetWelcomeText || 'Запитайте про меню, страви або дієти' }}
                            </p>
                        </div>

                        <div class="ai-modal__presets">
                            <button
                                v-for="preset in visiblePresets"
                                :key="preset"
                                class="ai-modal__preset"
                                :disabled="store.isLoading"
                                @click="sendPreset(preset)"
                            >
                                <svg class="ai-modal__preset-sparkle" width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2L14.09 8.26L20 9.27L15.55 13.97L16.91 20L12 16.9L7.09 20L8.45 13.97L4 9.27L9.91 8.26L12 2Z"/>
                                </svg>
                                {{ preset }}
                            </button>
                            <button
                                v-if="presets.length > 3 && !showAllPresets"
                                class="ai-modal__preset ai-modal__preset--more"
                                @click="showAllPresets = true"
                            >
                                + Показати більше
                            </button>
                        </div>
                    </template>

                    <ChatMessage
                        v-for="(msg, i) in store.messages"
                        :key="i"
                        :message="msg"
                        @chip-click="fillInput"
                        @confirm-order="handleConfirmOrder"
                    />

                    <div v-if="store.isLoading" class="ai-modal__typing">
                        <span class="dot" /><span class="dot" /><span class="dot" />
                    </div>
                </div>

                <transition name="toast-fade">
                    <div
                        v-if="store.orderSuccess || store.orderError"
                        class="ai-modal__toast"
                        :class="store.orderSuccess ? 'ai-modal__toast--ok' : 'ai-modal__toast--err'"
                    >
                        {{ store.orderSuccess || store.orderError }}
                    </div>
                </transition>

                <div class="ai-modal__footer">
                    <div class="ai-modal__input-wrap">
                        <textarea
                            ref="inputEl"
                            v-model="inputText"
                            class="ai-modal__input"
                            placeholder="Введіть питання..."
                            rows="1"
                            @keydown.enter.exact.prevent="sendMessage"
                            @keydown.enter.shift.exact="inputText += '\n'"
                            @input="autoResize"
                        />
                        <button
                            v-if="inputText.trim()"
                            class="ai-modal__input-btn"
                            :disabled="!canSend"
                            @click="sendMessage"
                            title="Надіслати (Enter)"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <line x1="22" y1="2" x2="11" y2="13"/>
                                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                            </svg>
                        </button>
                        <button
                            v-else
                            class="ai-modal__input-btn ai-modal__input-btn--mic"
                            :class="{ recording: isRecording }"
                            :disabled="!speechSupported"
                            @click="toggleRecording"
                            title="Голосовий ввід"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <rect x="9" y="2" width="6" height="11" rx="3"/>
                                <path d="M5 10a7 7 0 0 0 14 0"/>
                                <line x1="12" y1="19" x2="12" y2="22"/>
                                <line x1="9" y1="22" x2="15" y2="22"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </template>

            <!-- ═══════ VOICE TAB ═══════ -->
            <template v-else>
                <!-- Idle: big orb -->
                <div v-if="live.state.value === 'idle' || live.state.value === 'connecting'" class="ai-modal__voice">
                    <div
                        class="ai-modal__voice-orb"
                        :class="{ connecting: live.state.value === 'connecting' }"
                        @click="handleVoiceOrbClick"
                    >
                        <span class="ai-modal__voice-ring" />
                        <span class="ai-modal__voice-ring ai-modal__voice-ring--2" />
                        <svg v-if="live.state.value === 'idle'" class="ai-modal__voice-mic" width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                        <svg v-else class="ai-modal__voice-mic ai-modal__voice-spinner" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round">
                            <path d="M12 2a10 10 0 0 1 10 10" />
                        </svg>
                    </div>
                    <p class="ai-modal__voice-label">{{ liveStatusText }}</p>
                    <p v-if="live.error.value" class="ai-modal__voice-error">{{ live.error.value }}</p>
                </div>

                <!-- Active call: full transcript -->
                <template v-else>
                    <div class="ai-modal__voice-body" ref="transcriptEl">
                        <p
                            v-for="(line, i) in visibleTranscript"
                            :key="i"
                            class="ai-modal__vt-line"
                            :class="line.role === 'user' ? 'ai-modal__vt-user' : 'ai-modal__vt-ai'"
                        >{{ line.text }}</p>
                    </div>
                    <div class="ai-modal__voice-footer">
                        <span class="ai-modal__voice-status">{{ liveStatusText }}</span>
                        <button class="ai-modal__voice-hangup" @click="live.endCall()">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                                <path d="M6 6L18 18M6 18L18 6"/>
                            </svg>
                            Завершити
                        </button>
                    </div>
                </template>

                <transition name="toast-fade">
                    <div
                        v-if="store.orderSuccess || store.orderError"
                        class="ai-modal__toast"
                        :class="store.orderSuccess ? 'ai-modal__toast--ok' : 'ai-modal__toast--err'"
                    >
                        {{ store.orderSuccess || store.orderError }}
                    </div>
                </transition>
            </template>
        </div>
    </transition>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useAiAssistantStore } from '@/stores/aiAssistant'
import ChatMessage from './ChatMessage.vue'
import { useGeminiLive } from './composables/useGeminiLive'

const ORB_ICONS = {
    smiley: {
        type: 'svg',
        path: '<path d="M10 20C10 14.477 14.477 10 20 10H24C28.418 10 32 13.582 32 18V22C32 23.105 31.105 24 30 24H20C14.477 24 10 20.523 10 16V20Z" fill="white" fill-opacity="0.4"/><circle cx="17" cy="19" r="1.5" fill="white"/><circle cx="25" cy="19" r="1.5" fill="white"/><path d="M17.5 24C18 25 19.6 26 21 26C22.4 26 24 25 24.5 24" stroke="white" stroke-width="1.5" stroke-linecap="round"/>',
    },
    chat: {
        type: 'svg',
        path: '<path d="M28 22a2.67 2.67 0 0 1-2.67 2.67H14.67L10 29.33V12.67A2.67 2.67 0 0 1 12.67 10h12.66A2.67 2.67 0 0 1 28 12.67z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    },
    food: {
        type: 'svg',
        path: '<path d="M25 14h1a5 5 0 0 1 0 10h-1M8 14h16v11a4 4 0 0 1-4 4h-8a4 4 0 0 1-4-4V14z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="13" y1="8" x2="13" y2="11" stroke="white" stroke-width="2" stroke-linecap="round"/><line x1="17" y1="8" x2="17" y2="11" stroke="white" stroke-width="2" stroke-linecap="round"/><line x1="21" y1="8" x2="21" y2="11" stroke="white" stroke-width="2" stroke-linecap="round"/>',
    },
    headset: {
        type: 'svg',
        path: '<path d="M10 25v-7a10 10 0 0 1 20 0v7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M30 26a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM10 26a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2h-3z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    },
    bot: {
        type: 'svg',
        path: '<rect x="10" y="16" width="20" height="12" rx="2" stroke="white" stroke-width="2"/><circle cx="20" cy="11" r="2.5" stroke="white" stroke-width="2"/><line x1="20" y1="13.5" x2="20" y2="16" stroke="white" stroke-width="2"/><circle cx="15" cy="22" r="1.5" fill="white"/><circle cx="25" cy="22" r="1.5" fill="white"/>',
    },
    'emoji-sparkles': { type: 'emoji', emoji: '✨' },
    'emoji-wave':     { type: 'emoji', emoji: '👋' },
    'emoji-robot':    { type: 'emoji', emoji: '🤖' },
    'emoji-chat':     { type: 'emoji', emoji: '💬' },
    'emoji-food':     { type: 'emoji', emoji: '🍽️' },
    'emoji-bulb':     { type: 'emoji', emoji: '💡' },
    'emoji-star':     { type: 'emoji', emoji: '⭐' },
    'emoji-fire':     { type: 'emoji', emoji: '🔥' },
}

const store = useAiAssistantStore()
const live = useGeminiLive()

const welcomeIcon = computed(() =>
    ORB_ICONS[store.widgetIcon] || ORB_ICONS.smiley
)

const inputText = ref('')
const messagesEl = ref(null)
const inputEl = ref(null)
const transcriptEl = ref(null)
const showAllPresets = ref(false)

const presets = [
    'Яке сьогодні меню?',
    'Які є вікові категорії?',
    'Покажи страви до 30 грн',
    'Що можна дітям на дієті?',
    'Які супи є в меню?',
    'Є вегетаріанські страви?',
]

const visiblePresets = computed(() =>
    showAllPresets.value ? presets : presets.slice(0, 3)
)

const tabIndicatorStyle = computed(() => ({
    transform: store.activeTab === 'voice' ? 'translateX(100%)' : 'translateX(0)',
}))

const isRecording = ref(false)
const speechSupported =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)

let recognition = null

const voiceStatusText = computed(() => {
    if (store.isSpeaking) return 'Відтворюю...'
    if (isRecording.value) return 'Слухаю...'
    return 'Натисніть щоб говорити'
})

const liveStatusText = computed(() => {
    if (live.state.value === 'connecting') return 'З\'єднання...'
    if (live.state.value === 'active' && live.aiSpeaking.value) return 'AI говорить...'
    if (live.state.value === 'active' && live.userSpeaking.value) return 'Слухаю вас...'
    if (live.state.value === 'active') return 'Розмова активна'
    return 'Натисніть для дзвінка'
})

const visibleTranscript = computed(() => {
    const lines = live.transcriptLines.value
    if (lines.length <= 3) return lines
    return lines.slice(-3)
})

function handleVoiceOrbClick() {
    if (live.state.value === 'idle') {
        live.startCall()
    }
}

function toggleRecording() {
    if (isRecording.value) {
        recognition?.stop()
        return
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR) return
    recognition = new SR()
    recognition.lang = 'uk-UA'
    recognition.interimResults = false
    recognition.onresult = (e) => {
        const transcript = e.results[0][0].transcript
        if (store.activeTab === 'voice') {
            store.sendMessage(transcript)
        } else {
            inputText.value = (inputText.value + ' ' + transcript).trim()
        }
    }
    recognition.onend = () => { isRecording.value = false }
    recognition.onerror = () => { isRecording.value = false }
    recognition.start()
    isRecording.value = true
}

const canSend = computed(() => inputText.value.trim().length > 0 && !store.isLoading)

async function sendMessage() {
    const text = inputText.value.trim()
    if (!text || store.isLoading) return
    inputText.value = ''
    await nextTick()
    if (inputEl.value) inputEl.value.style.height = 'auto'
    await store.sendMessage(text)
}

async function sendPreset(text) {
    if (store.isLoading) return
    await store.sendMessage(text)
}

function fillInput(text) {
    const current = inputText.value
    const sep = current.length > 0 && !/[\s,]$/.test(current) ? ' ' : ''
    inputText.value = current + sep + text
    nextTick(() => {
        if (inputEl.value) {
            inputEl.value.focus()
            inputEl.value.style.height = 'auto'
            inputEl.value.style.height = Math.min(inputEl.value.scrollHeight, 120) + 'px'
            const len = inputEl.value.value.length
            inputEl.value.setSelectionRange(len, len)
        }
    })
}

async function handleConfirmOrder(orderData) {
    if (store.orderConfirming) return
    const result = await store.confirmOrder(orderData)
    if (result.success) {
        const dishList = orderData.dishes
            .map(d => `${d.name}, ${d.quant} порцій`)
            .join('; ')
        const total = orderData.dishes.reduce((s, d) => s + (d.price || 0) * d.quant, 0)
        const lunchLabel = { 1: 'обід', 2: 'сніданок', 6: 'підвечірок', 7: 'вечеря' }[orderData.lunchId] || ''
        const ttsText = `Замовлення записано. ${orderData.clientName}, ${orderData.date}, ${lunchLabel}. ${dishList}. Сума: ${total} гривень.`
        store.speak(ttsText)
        setTimeout(() => { store.orderSuccess = null }, 4000)
    } else {
        store.speak(`Помилка: ${store.orderError}`)
        setTimeout(() => { store.orderError = null }, 5000)
    }
}

function resetChat() {
    store.clearHistory()
    showAllPresets.value = false
    nextTick(() => inputEl.value?.focus())
}

function autoResize(e) {
    const el = e.target
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 120) + 'px'
}

watch(
    () => store.messages.length,
    async () => {
        await nextTick()
        if (messagesEl.value) {
            messagesEl.value.scrollTop = messagesEl.value.scrollHeight
        }
    }
)

watch(
    () => store.isOpen,
    async (open) => {
        if (open) {
            await nextTick()
            inputEl.value?.focus()
        } else {
            live.endCall()
        }
    }
)

watch(
    () => store.activeTab,
    (tab) => { if (tab !== 'voice') live.endCall() }
)

watch(
    () => live.transcriptLines.value.length,
    async () => {
        await nextTick()
        if (transcriptEl.value) {
            transcriptEl.value.scrollTop = transcriptEl.value.scrollHeight
        }
    }
)
</script>

<style scoped lang="scss">
/* ══════════════════════════════════════════
   Modal container
   ══════════════════════════════════════════ */
.ai-modal {
    position: fixed;
    bottom: 84px;
    right: 20px;
    width: 400px;
    max-height: 600px;
    background: #fff;
    border-radius: var(--widget-radius, 20px);
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.16), 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    z-index: 3100;
    overflow: hidden;
}

/* ── Header ─────────────────────────────── */
.ai-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px 6px;
    flex-shrink: 0;
}

.ai-modal__hbtn {
    background: none;
    border: none;
    color: #666;
    cursor: pointer;
    padding: 6px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s, background 0.15s;
    width: 30px;
    height: 30px;

    &:hover { background: rgba(0, 0, 0, 0.06); color: #333; }
    &.active { color: var(--widget-primary, #f0a500); }
    &.speaking { animation: hbtn-pulse 1s ease-in-out infinite; }
    &--spacer { visibility: hidden; pointer-events: none; }
}

.ai-modal__hright {
    display: flex;
    align-items: center;
    gap: 2px;
}

/* ── Tab bar ────────────────────────────── */
.ai-modal__tabs {
    position: relative;
    display: flex;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 10px;
    padding: 3px;
}

.ai-modal__tab {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 5px;
    border: none;
    background: none;
    padding: 6px 16px;
    font-size: 12.5px;
    font-weight: 600;
    color: #888;
    cursor: pointer;
    border-radius: 8px;
    transition: color 0.25s;
    font-family: inherit;
    white-space: nowrap;

    &.active { color: var(--widget-primary, #f0a500); }
}

.ai-modal__tab-indicator {
    position: absolute;
    top: 3px;
    left: 3px;
    width: calc(50% - 3px);
    height: calc(100% - 6px);
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
}

/* ── Body / messages ────────────────────── */
.ai-modal__body {
    flex: 1;
    padding: 12px 16px;
    overflow-y: auto;
    min-height: 200px;
    max-height: 420px;
}

/* ── Welcome screen ─────────────────────── */
.ai-modal__welcome {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 24px 12px 16px;
    margin: -12px -16px 12px;
    background: linear-gradient(
        180deg,
        color-mix(in srgb, var(--widget-primary, #f0a500) 12%, white) 0%,
        white 100%
    );
    border-radius: 0 0 24px 24px;
}

.ai-modal__welcome-orb {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--widget-primary, #f0a500), color-mix(in srgb, var(--widget-primary, #f0a500) 65%, #e85d75));
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
    box-shadow: 0 4px 20px color-mix(in srgb, var(--widget-primary, #f0a500) 30%, transparent);
    transform-origin: center center;
    animation: welcome-orb-breathe 2.8s ease-in-out infinite;

    svg {
        width: 36px;
        height: 36px;
        transform-origin: center center;
        animation: welcome-orb-inner-pulse 2.8s ease-in-out infinite;

        @media (prefers-reduced-motion: reduce) {
            animation: none;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
}

.ai-modal__welcome-emoji {
    font-size: 32px;
    line-height: 1;
    display: block;
    animation: welcome-orb-emoji-glow 2.8s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
}

.ai-modal__welcome-hi {
    font-size: 20px;
    font-weight: 700;
    color: #333;
    margin: 0 0 2px;
}

.ai-modal__welcome-name {
    font-size: 15px;
    color: #555;
    margin: 0 0 6px;
    strong { color: var(--widget-primary, #f0a500); }
}

.ai-modal__welcome-sub {
    font-size: 12.5px;
    color: #999;
    margin: 0;
}

/* ── Presets ─────────────────────────────── */
.ai-modal__presets {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    padding: 0 2px;
}

.ai-modal__preset {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #fff;
    border: 1.5px solid color-mix(in srgb, var(--widget-primary, #f0a500) 40%, #ddd);
    border-radius: 20px;
    color: color-mix(in srgb, var(--widget-primary, #f0a500) 85%, #333);
    font-size: 12px;
    font-weight: 500;
    padding: 7px 14px;
    cursor: pointer;
    transition: background 0.2s, color 0.2s, border-color 0.2s;
    font-family: inherit;
    text-align: left;
    line-height: 1.3;

    &:hover:not(:disabled) {
        background: var(--widget-primary, #f0a500);
        border-color: var(--widget-primary, #f0a500);
        color: #fff;
        .ai-modal__preset-sparkle { opacity: 1; }
    }

    &:disabled { opacity: 0.5; cursor: default; }

    &--more {
        border-style: dashed;
        color: #999;
        .ai-modal__preset-sparkle { display: none; }
        &:hover:not(:disabled) {
            background: rgba(0, 0, 0, 0.04);
            color: #666;
            border-color: #bbb;
        }
    }
}

.ai-modal__preset-sparkle {
    flex-shrink: 0;
    opacity: 0.55;
    transition: opacity 0.2s;
}

/* ── Typing indicator ───────────────────── */
.ai-modal__typing {
    display: flex;
    gap: 4px;
    padding: 6px 0;

    .dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #bbb;
        animation: blink 1.2s infinite;
        &:nth-child(2) { animation-delay: 0.2s; }
        &:nth-child(3) { animation-delay: 0.4s; }
    }
}

/* ── Toast ───────────────────────────────── */
.ai-modal__toast {
    margin: 0 16px 6px;
    padding: 8px 12px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 500;
    flex-shrink: 0;

    &--ok {
        background: #e8f5e9;
        color: #2e7d32;
        border: 1px solid #a5d6a7;
    }

    &--err {
        background: #fdecea;
        color: #c62828;
        border: 1px solid #ef9a9a;
    }
}

/* ── Footer / input ─────────────────────── */
.ai-modal__footer {
    padding: 10px 14px 12px;
    flex-shrink: 0;
}

.ai-modal__input-wrap {
    position: relative;
    display: flex;
    align-items: flex-end;
    background: #f5f5f5;
    border: 1.5px solid #e8e8e8;
    border-radius: 22px;
    transition: border-color 0.2s, box-shadow 0.2s;

    &:focus-within {
        border-color: var(--widget-primary, #f0a500);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--widget-primary, #f0a500) 12%, transparent);
    }
}

.ai-modal__input {
    flex: 1;
    resize: none;
    border: none;
    background: transparent;
    padding: 10px 6px 10px 16px;
    font-size: 13px;
    font-family: inherit;
    outline: none;
    line-height: 1.4;
    max-height: 120px;
    overflow-y: auto;
}

.ai-modal__input-btn {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    margin: 3px 4px 3px 0;
    border: none;
    border-radius: 50%;
    background: var(--widget-primary, #f0a500);
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s, transform 0.15s;

    &:hover:not(:disabled) { opacity: 0.85; transform: scale(1.05); }
    &:disabled { opacity: 0.35; cursor: default; }

    &--mic.recording {
        animation: mic-pulse 1.2s ease-in-out infinite;
        background: #e53935;
    }
}

/* ══════════════════════════════════════════
   Voice screen
   ══════════════════════════════════════════ */
.ai-modal__voice {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px 48px;
    min-height: 360px;
    background: linear-gradient(
        180deg,
        color-mix(in srgb, var(--widget-primary, #f0a500) 14%, white) 0%,
        white 70%
    );
}

.ai-modal__voice-orb {
    position: relative;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--widget-primary, #f0a500), color-mix(in srgb, var(--widget-primary, #f0a500) 60%, #e85d75));
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.2s;
    box-shadow: 0 6px 30px color-mix(in srgb, var(--widget-primary, #f0a500) 35%, transparent);

    &:hover { transform: scale(1.04); }
}

.ai-modal__voice-ring {
    position: absolute;
    inset: -12px;
    border-radius: 50%;
    border: 2px solid color-mix(in srgb, var(--widget-primary, #f0a500) 25%, transparent);
    opacity: 0;
    transition: opacity 0.3s;

    .pulsing &, .speaking & {
        opacity: 1;
        animation: voice-ring 1.8s ease-out infinite;
    }

    &--2 {
        inset: -24px;
        .pulsing &, .speaking & {
            animation-delay: 0.4s;
        }
    }
}

.ai-modal__voice-mic {
    position: relative;
    z-index: 1;
}

.ai-modal__voice-label {
    margin-top: 28px;
    font-size: 15px;
    font-weight: 600;
    color: #666;
    letter-spacing: 0.02em;
}

/* ── Live call transcript ────────────── */
.ai-modal__voice-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 10px;
    min-height: 200px;
    max-height: 420px;
}

.ai-modal__vt-line {
    margin: 0;
    font-size: 13.5px;
    line-height: 1.5;
    animation: vt-fadein 0.3s ease;
}

.ai-modal__vt-ai {
    color: #444;
    text-align: left;
}

.ai-modal__vt-user {
    color: var(--widget-primary, #f0a500);
    text-align: right;
    font-weight: 500;
}

.ai-modal__voice-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px 14px;
    flex-shrink: 0;
    border-top: 1px solid #f0f0f0;
}

.ai-modal__voice-status {
    font-size: 12px;
    color: #999;
    font-weight: 500;
}

/* ── Header live dot ─────────────────── */
.ai-modal__live-dot {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;

    &::before {
        content: '';
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #4caf50;
        animation: live-dot-pulse 1.5s ease-in-out infinite;
    }

    &.speaking::before {
        background: #4caf50;
        animation: live-dot-pulse 0.8s ease-in-out infinite;
    }

    &.listening::before {
        background: var(--widget-primary, #f0a500);
        animation: live-dot-pulse 0.6s ease-in-out infinite;
    }
}

.ai-modal__voice-error {
    margin-top: 8px;
    font-size: 12px;
    color: #c62828;
    text-align: center;
}

.ai-modal__voice-hangup {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 16px;
    border: none;
    border-radius: 20px;
    background: #e53935;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: background 0.2s, transform 0.15s;

    &:hover { background: #c62828; transform: scale(1.03); }
}

.ai-modal__voice-orb.in-call {
    background: linear-gradient(135deg, #4caf50, #2e7d32);
    box-shadow: 0 6px 30px rgba(76, 175, 80, 0.35);
}

.ai-modal__voice-orb.connecting {
    opacity: 0.7;
    cursor: default;
}

.ai-modal__voice-spinner {
    animation: voice-spinner 0.8s linear infinite;
}

/* ══════════════════════════════════════════
   Animations & keyframes
   ══════════════════════════════════════════ */

/* Modal enter: scale + opacity */
.modal-pop-enter-active {
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.modal-pop-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}
.modal-pop-enter-from {
    opacity: 0;
    transform: scale(0.95) translateY(8px);
}
.modal-pop-leave-to {
    opacity: 0;
    transform: scale(0.97) translateY(6px);
}

.toast-fade-enter-active,
.toast-fade-leave-active {
    transition: opacity 0.3s;
}
.toast-fade-enter-from,
.toast-fade-leave-to {
    opacity: 0;
}

@keyframes blink {
    0%, 80%, 100% { opacity: 0.2; }
    40% { opacity: 1; }
}

@keyframes mic-pulse {
    0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(229, 57, 53, 0.4); }
    50% { transform: scale(1.08); box-shadow: 0 0 0 8px rgba(229, 57, 53, 0); }
}

@keyframes voice-ring {
    0% { transform: scale(0.95); opacity: 0.6; }
    100% { transform: scale(1.3); opacity: 0; }
}

@keyframes hbtn-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.15); }
}

/* Welcome orb: gentle “breathing” pulse */
@keyframes welcome-orb-breathe {
    0%, 100% {
        transform: scale(1);
        box-shadow:
            0 4px 20px color-mix(in srgb, var(--widget-primary, #f0a500) 28%, transparent),
            0 0 0 0 color-mix(in srgb, var(--widget-primary, #f0a500) 35%, transparent);
    }
    50% {
        transform: scale(1.07);
        box-shadow:
            0 8px 28px color-mix(in srgb, var(--widget-primary, #f0a500) 42%, transparent),
            0 0 0 8px color-mix(in srgb, var(--widget-primary, #f0a500) 0%, transparent);
    }
}

@keyframes welcome-orb-emoji-glow {
    0%, 100% { filter: brightness(1); transform: scale(1); }
    50% { filter: brightness(1.12); transform: scale(1.04); }
}

@keyframes vt-fadein {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes live-dot-pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.4); opacity: 0.7; }
}

@keyframes voice-spinner {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

@keyframes welcome-orb-inner-pulse {
    0%, 100% { filter: brightness(1); opacity: 0.96; }
    50% { filter: brightness(1.15); opacity: 1; }
}
</style>
